<template>
  <v-container class="py-6" fluid>
    <div class="d-flex align-center mb-6">
      <v-btn icon color="primary" class="mr-3 bg-primary-light" elevation="2">
        <v-icon>mdi-account-school</v-icon>
      </v-btn>
      <div>
        <h1 class="text-h5 font-weight-bold primary--text mb-0">
          {{ $t('extracurricular_activity_teacher') }} (Drag & Drop)
        </h1>
        <span class="text-caption grey--text font-weight-medium">
          Asigna maestros arrastrándolos a la actividad deseada
        </span>
      </div>
    </div>

    <v-row>
      <!-- LEFT SECTION: TEACHERS -->
      <v-col cols="12" md="6">
        <v-card class="elevation-12 rounded-xl overflow-hidden glass-card fill-height">
          <v-toolbar flat color="transparent" class="px-4 pt-4">
            <v-icon left color="primary">mdi-account-tie</v-icon>
            <v-toolbar-title class="font-weight-bold primary--text">Maestros</v-toolbar-title>
          </v-toolbar>

          <v-card-text class="pa-6">
            <v-text-field
              v-model="searchTeacher"
              append-icon="mdi-magnify"
              :label="$t('Buscar')"
              single-line
              hide-details
              outlined
              dense
              class="rounded-lg bg-grey-lighten-4 mb-6"
            ></v-text-field>

            <v-divider class="mb-4"></v-divider>

            <div class="list-container custom-scrollbar">
              <v-progress-linear v-if="loadingTeachers" indeterminate color="primary"></v-progress-linear>
              
              <v-row v-if="filteredTeachers.length > 0">
                <v-col 
                  v-for="teacher in filteredTeachers" 
                  :key="teacher.id" 
                  cols="12"
                  class="py-1"
                >
                  <div 
                    class="teacher-card d-flex align-center pa-3 rounded-lg border shadow-xs"
                    draggable="true"
                    @dragstart="onDragStart($event, teacher)"
                  >
                    <v-avatar size="40" class="mr-3 elevation-1">
                      <v-img :src="teacher.idUser.avatar || defaultAvatar"></v-img>
                    </v-avatar>
                    <div class="flex-grow-1">
                      <div class="font-weight-bold text-subtitle-2">{{ teacher.idUser.first_name }} {{ teacher.idUser.last_name }}</div>
                      <div class="text-caption grey--text">{{ teacher.idUser.email || 'Sin correo' }}</div>
                    </div>
                    <v-icon color="grey lighten-1">mdi-drag-variant</v-icon>
                  </div>
                </v-col>
              </v-row>
              
              <div v-else-if="!loadingTeachers" class="text-center py-12">
                <v-icon size="64" color="grey lighten-3">mdi-account-search-outline</v-icon>
                <p class="grey--text mt-4">No se encontraron maestros</p>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- RIGHT SECTION: ACTIVITY ASSIGNMENT -->
      <v-col cols="12" md="6">
        <v-card 
          class="elevation-12 rounded-xl overflow-hidden glass-card fill-height"
          :class="{'drop-zone-active': isDraggingOver}"
          @dragover.prevent="isDraggingOver = true"
          @dragleave="isDraggingOver = false"
          @drop="onDrop"
        >
          <v-toolbar flat color="transparent" class="px-4 pt-4">
            <v-icon left color="secondary">mdi-star-circle</v-icon>
            <v-toolbar-title class="font-weight-bold secondary--text">Actividad Extracurricular</v-toolbar-title>
          </v-toolbar>

          <v-card-text class="pa-6">
            <v-autocomplete
              v-model="selectedActivityId"
              :items="activities"
              item-text="name"
              item-value="idExtracurricularActivity"
              :label="$t('placeholder_choose_extracurricular_activity')"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-star"
              class="rounded-lg bg-grey-lighten-4 mb-6"
              @change="getAssignmentsByActivity"
            >
              <template v-slot:item="data">
                <v-list-item-icon>
                  <v-icon :color="getActivityColor(data.item.idExtracurricularActivity)">
                    {{ getActivityIcon(data.item.idExtracurricularActivity) }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ getBaseActivityName(data.item.idActivity) }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>

            <v-divider class="mb-4"></v-divider>

            <div 
              class="drop-area custom-scrollbar"
              :class="{'has-activity': !!selectedActivityId}"
            >
              <div v-if="!selectedActivityId" class="text-center py-12">
                <v-icon size="64" color="grey lighten-3">mdi-star-outline</v-icon>
                <p class="grey--text mt-4">Selecciona una actividad para asignar maestros</p>
              </div>

              <div v-else>
                <div class="d-flex align-center mb-4">
                  <v-chip 
                    :color="getActivityColor(selectedActivityId)" 
                    dark 
                    class="font-weight-bold"
                  >
                    <v-icon left>{{ getActivityIcon(selectedActivityId) }}</v-icon>
                    {{ getActivityName(selectedActivityId) }}
                  </v-chip>
                  <v-spacer></v-spacer>
                  <span class="text-caption font-weight-bold grey--text">
                    {{ assignmentList.length }} MAESTROS ASIGNADOS
                  </span>
                </div>

                <v-progress-linear v-if="loadingAssignments" indeterminate color="secondary" class="mb-4"></v-progress-linear>

                <div v-if="assignmentList.length > 0">
                  <v-row>
                    <v-col 
                      v-for="assignment in assignmentList" 
                      :key="assignment.idExtracurricularActivityTeacher" 
                      cols="12"
                      class="py-1"
                    >
                      <v-hover v-slot="{ hover }">
                        <div class="enrolled-card d-flex align-center pa-3 rounded-lg border shadow-xs transition-all" :class="{'elevation-4': hover}">
                          <v-avatar size="40" class="mr-3 elevation-1">
                            <v-img :src="getTeacherAvatar(assignment.idTeacher) || defaultAvatar"></v-img>
                          </v-avatar>
                          <div class="flex-grow-1">
                            <div class="font-weight-bold text-subtitle-2">{{ getTeacherName(assignment.idTeacher) }}</div>
                            <div class="text-caption grey--text">Asignado</div>
                          </div>
                          <div class="d-flex">
                            <v-btn 
                              icon 
                              small 
                              color="secondary" 
                              class="mr-1 opacity-0 transition-all shadow-sm bg-white" 
                              :class="{'opacity-100': hover}"
                              @click="viewScheduleByActivity(selectedActivityId)"
                            >
                              <v-icon small>mdi-calendar-clock</v-icon>
                            </v-btn>
                            <v-btn 
                              icon 
                              small 
                              color="error" 
                              class="opacity-0 transition-all shadow-sm bg-white" 
                              :class="{'opacity-100': hover}"
                              @click="confirmDelete(assignment)"
                            >
                              <v-icon small>mdi-trash-can-outline</v-icon>
                            </v-btn>
                          </div>
                        </div>
                      </v-hover>
                    </v-col>
                  </v-row>
                </div>
                
                <div v-else-if="!loadingAssignments" class="text-center py-12 drop-instruction border-dashed rounded-xl border-2">
                  <v-icon size="48" color="primary" class="animate-bounce">mdi-tray-arrow-down</v-icon>
                  <p class="primary--text font-weight-bold mt-4">Arrastra maestros aquí para asignarlos</p>
                  <span class="text-caption grey--text">Solo suelta un maestro para completar el proceso</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog Schedules -->
    <v-dialog v-model="dialogSchedules" max-width="500px">
      <v-card class="rounded-xl overflow-hidden glass-card shadow-2xl">
        <v-toolbar flat color="secondary" dark>
          <v-toolbar-title class="font-weight-bold">
            <v-icon left>mdi-calendar-clock</v-icon>
            {{ $t('schedules') }}: {{ selectedActivityName }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogSchedules = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <v-data-table
            :headers="headersSchedules"
            :items="schedules"
            :loading="loadingSchedules"
            hide-default-footer
            class="elevation-0 custom-table simple-table"
            :no-data-text="$t('no_registros')"
          >
            <template v-slot:item.dayWeek="{ item }">
              <span class="font-weight-bold">{{ getDayName(item.dayWeek) }}</span>
            </template>
            <template v-slot:item.time_range="{ item }">
              <v-chip outlined small color="secondary" class="font-weight-medium">
                <v-icon left x-small>mdi-clock-outline</v-icon>
                {{ item.starTime.substring(0, 5) }} - {{ item.endTime.substring(0, 5) }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn color="primary" text class="rounded-pill px-6" @click="dialogSchedules = false">
            {{ $t('cerrar') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Delete Dialog -->
    <v-dialog v-model="dialogDelete" max-width="450px" transition="dialog-center-transition">
      <v-card class="rounded-xl overflow-hidden glass-card p-4">
        <v-card-title class="headline d-flex align-center pb-2">
          <v-icon color="error" large class="mr-3">mdi-alert-circle-outline</v-icon>
          <span class="font-weight-bold">{{ $t('aviso') }}</span>
        </v-card-title>

        <v-card-text class="pt-4 text-body-1 dark--text font-weight-medium">
          {{ $t('msg_delete_activity_teacher') }}
          <div v-if="itemToDelete" class="mt-4 pa-4 bg-grey-lighten-4 rounded-lg border-l-4-error shadow-inner">
            <div class="d-flex align-center mb-2">
              <v-icon x-small color="grey" class="mr-2">mdi-account</v-icon>
              <span class="text-caption font-weight-bold grey--text text-uppercase tracking-wider">Maestro</span>
            </div>
            <div class="font-weight-bold primary--text mb-3">
              {{ getTeacherName(itemToDelete.idTeacher) }}
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn text class="rounded-pill px-6" @click="dialogDelete = false">No</v-btn>
          <v-btn
            color="error"
            class="rounded-pill px-8 elevation-4 shadow-error hover-scale transition-all"
            @click="deleteItemConfirm"
            :loading="deleting"
          >
            {{ $t('Si') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { HTTP } from '@/main.js'
import store from '@/store'
import { mapGetters } from 'vuex'

export default {
  name: 'ExtracurricularActivityTeacherDragDrop',
  data: () => ({
    searchTeacher: '',
    loadingData: false,
    loadingTeachers: false,
    loadingAssignments: false,
    loadingActivities: false,
    deleting: false,
    
    selectedActivityId: null,
    isDraggingOver: false,

    allTeachers: [],
    activities: [],
    baseActivities: [],
    assignmentList: [],
    
    dialogDelete: false,
    itemToDelete: null,
    dialogSchedules: false,
    loadingSchedules: false,
    schedules: [],
    selectedActivityName: '',
    
    days: [
      { name: 'Lunes', value: 0 }, { name: 'Martes', value: 1 }, { name: 'Miércoles', value: 2 },
      { name: 'Jueves', value: 3 }, { name: 'Viernes', value: 4 }, { name: 'Sábado', value: 5 },
      { name: 'Domingo', value: 6 }
    ],
    
    defaultAvatar: 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'
  }),

  computed: {
    ...mapGetters(['getProfile']),
    filteredTeachers() {
      let filtered = this.allTeachers;
      
      if (this.searchTeacher) {
        const query = this.searchTeacher.toLowerCase();
        filtered = filtered.filter(t => 
          (t.idUser.first_name + ' ' + t.idUser.last_name).toLowerCase().includes(query) ||
          (t.idUser.email && t.idUser.email.toLowerCase().includes(query))
        );
      }
      
      // Filter out teachers already assigned to selected activity
      if (this.selectedActivityId) {
        filtered = filtered.filter(t => 
          !this.assignmentList.some(a => a.idTeacher === t.id)
        );
      }
      
      return filtered;
    },
    headersSchedules() {
      return [
        { text: this.$t('day'), value: 'dayWeek', align: 'start' },
        { text: this.$t('schedule'), value: 'time_range', align: 'center', sortable: false }
      ]
    }
  },

  created() {
    this.init();
  },

  methods: {
    async init() {
      this.loadingData = true;
      try {
        await Promise.all([
          this.getTeachers(),
          this.getActivities(),
          this.getBaseActivities()
        ]);
        // Initially no assignments shown until activity selected
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        this.loadingData = false;
      }
    },

    getTeachers() {
      this.loadingTeachers = true;
      return HTTP.get('teacher/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.allTeachers = res.data;
      }).finally(() => { this.loadingTeachers = false; });
    },

    getActivities() {
      this.loadingActivities = true;
      return HTTP.get('extracurricular_activities_by_school/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.activities = res.data;
      }).finally(() => { this.loadingActivities = false; });
    },

    getBaseActivities() {
      return HTTP.get('activities_by_school/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.baseActivities = res.data;
      });
    },

    getAssignmentsByActivity() {
      if (!this.selectedActivityId) {
        this.assignmentList = [];
        return;
      }
      this.loadingAssignments = true;
      // Note: We use the filter by activity endpoint documented/pattern
      const url = `/extracurricular_activity_by_teacher/by_activity/${this.selectedActivityId}/`;

      return HTTP.get(url, {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.assignmentList = res.data;
      }).catch(err => {
        console.error('Error fetching assignments:', err);
        // If specific filtered endpoint fails, we might need to fetch all and filter client side
        return this.getAssignmentsAllAndFilter();
      }).finally(() => { this.loadingAssignments = false; });
    },

    getAssignmentsAllAndFilter() {
      return HTTP.get('/extracurricular_activity_by_teacher/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.assignmentList = res.data.filter(a => a.idExtracurricularActivity === this.selectedActivityId);
      });
    },

    getTeacherName(idTeacher) {
      const teacher = this.allTeachers.find(t => t.id === idTeacher);
      return teacher && teacher.idUser ? `${teacher.idUser.first_name} ${teacher.idUser.last_name}` : 'Cargando...';
    },

    getTeacherAvatar(idTeacher) {
      const teacher = this.allTeachers.find(t => t.id === idTeacher);
      return teacher && teacher.idUser ? teacher.idUser.avatar : null;
    },

    getActivityName(idActivity) {
      const activity = this.activities.find(a => a.idExtracurricularActivity === idActivity);
      return activity ? activity.name : '';
    },

    getBaseActivityName(idBaseActivity) {
      const base = this.baseActivities.find(b => b.idActivity === idBaseActivity);
      return base ? base.name : '';
    },

    getActivityIcon(idActivity) {
      const activity = this.activities.find(a => a.idExtracurricularActivity === idActivity);
      if (!activity) return 'mdi-star';
      const base = this.baseActivities.find(b => b.idActivity === activity.idActivity);
      return base && base.workshop ? 'mdi-palette' : 'mdi-run-fast';
    },

    getActivityColor(idActivity) {
      const activity = this.activities.find(a => a.idExtracurricularActivity === idActivity);
      if (!activity) return 'grey';
      const base = this.baseActivities.find(b => b.idActivity === activity.idActivity);
      return base && base.workshop ? 'success' : 'info';
    },

    getDayName(value) {
      const day = this.days.find(d => d.value === value);
      return day ? day.name : '';
    },

    viewScheduleByActivity(idActivity) {
      this.selectedActivityName = this.getActivityName(idActivity);
      this.loadingSchedules = true;
      this.dialogSchedules = true;
      HTTP.get(`/extracurricular_activity_schedules_by_activity/${idActivity}/`, {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.schedules = res.data;
      }).finally(() => { this.loadingSchedules = false; });
    },

    // DRAG AND DROP HANDLERS
    onDragStart(event, teacher) {
      event.dataTransfer.setData('teacherId', teacher.id);
      event.dataTransfer.effectAllowed = 'copy';
    },

    onDrop(event) {
      this.isDraggingOver = false;
      const teacherId = event.dataTransfer.getData('teacherId');
      
      if (!teacherId) return;
      if (!this.selectedActivityId) {
        this.showSnackbar(this.$t('placeholder_choose_extracurricular_activity'), 'warning');
        return;
      }

      this.assignTeacher(teacherId, this.selectedActivityId);
    },

    assignTeacher(idTeacher, idExtracurricularActivity) {
      if (this.assignmentList.some(a => a.idTeacher === idTeacher)) {
        this.showSnackbar(this.$t('msg_duplicate_assignment'), 'warning');
        return;
      }

      this.loadingAssignments = true;
      HTTP.post('/extracurricular_activity_by_teacher/', {
        idTeacher,
        idExtracurricularActivity
      }, {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(() => {
        this.showSnackbar(this.$t('msg_activity_teacher_added'), 'success');
        this.getAssignmentsByActivity();
      }).catch(err => {
        console.error('Error assigning teacher:', err);
        this.showSnackbar('Error al asignar al maestro', 'error');
        this.loadingAssignments = false;
      });
    },

    confirmDelete(assignment) {
      this.itemToDelete = assignment;
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      this.deleting = true;
      const id = this.itemToDelete.idExtracurricularActivityTeacher || this.itemToDelete.idExtracurricularActivityByTeacher;
      
      HTTP.delete(`extracurricular_activity_by_teacher/${id}/`, {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(() => {
        this.showSnackbar(this.$t('msg_activity_teacher_deleted'), 'success');
        this.getAssignmentsByActivity();
        this.dialogDelete = false;
      }).catch(err => {
        console.error('Error deleting assignment:', err);
        this.showSnackbar('Error al eliminar asignación', 'error');
      }).finally(() => { this.deleting = false; });
    },

    clear() {
      this.$store.commit('snackbar_activate_false')
    },

    showSnackbar(message, color) {
      this.$store.commit('snackbar_change_message', message)
      this.$store.commit('snackbar_change_color', color)
      this.$store.commit('snackbar_activate_true')
      setTimeout(this.clear, 3000)
    }
  }
}
</script>

<style scoped>
.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.bg-primary-light {
  background-color: rgba(var(--v-primary-base), 0.08) !important;
}

.bg-grey-lighten-4 {
  background-color: #f8f9fa !important;
}

.shadow-xs {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
}

.list-container, .drop-area {
  height: 500px;
  overflow-y: auto;
  padding: 4px;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #bdbdbd;
}

.teacher-card {
  border: 1px solid #edf2f7;
  background-color: white;
  transition: all 0.2s ease;
  cursor: grab;
}
.teacher-card:hover {
  border-color: var(--v-primary-base);
  background-color: rgba(var(--v-primary-base), 0.02);
  transform: translateX(4px);
}
.teacher-card:active {
  cursor: grabbing;
}

.enrolled-card {
  border: 1px solid #edf2f7;
  background-color: white;
}

.drop-zone-active {
  background-color: rgba(var(--v-primary-base), 0.05) !important;
  border: 2px dashed var(--v-primary-base) !important;
}

.drop-instruction {
  border-color: #e2e8f0;
  background-color: #f8fafc;
}

.border-dashed {
  border-style: dashed !important;
}

.border-2 {
  border-width: 2px !important;
}

.opacity-0 { opacity: 0; }
.opacity-100 { opacity: 1; }

.animate-bounce {
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
  40% {transform: translateY(-20px);}
  60% {transform: translateY(-10px);}
}

.hover-scale:hover {
  transform: translateY(-2px);
}

.shadow-error {
  box-shadow: 0 4px 14px 0 rgba(255, 82, 82, 0.3) !important;
}

.border-l-4-error {
  border-left: 4px solid #ff5252;
}

.tracking-wider {
  letter-spacing: 0.5px;
}

.shadow-inner {
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05);
}

.shadow-sm {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
}
</style>
